import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { AliasBadge, StatusBadge, TrustBadge, VerificationBadge } from '../components/Badges';
import { TrustLevelBadge } from '../components/TrustLevelBadge';
import { Users, Tag, Receipt, AlertTriangle, Eye, Shield, Building2, FileCheck, DollarSign, FileText, MessageSquareWarning, ShieldCheck, Activity, Search } from 'lucide-react';
import { toast } from 'sonner';
import { AdminOpsOverview, AdminTransactionInspector } from '../components/AdminOps';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [sources, setSources] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [flaggedMsgs, setFlaggedMsgs] = useState([]);
  const [verifyQueue, setVerifyQueue] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [conflictConfirm, setConflictConfirm] = useState(null); // {conflict_id, resolution}
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewLayer, setReviewLayer] = useState('kyc');
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewReason, setReviewReason] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [u, l, t, d, s, p, a, f, v, c] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/leads'),
        api.get('/admin/transactions'),
        api.get('/admin/disputes'),
        api.get('/admin/sources'),
        api.get('/admin/payouts'),
        api.get('/admin/audit-logs?limit=200'),
        api.get('/admin/flagged-messages'),
        api.get('/admin/verify/queue'),
        api.get('/admin/conflicts'),
      ]);
      setUsers(u.data); setLeads(l.data); setTransactions(t.data); setDisputes(d.data);
      setSources(s.data); setPayouts(p.data); setAuditLogs(a.data); setFlaggedMsgs(f.data);
      setVerifyQueue(v.data);
      setConflicts(c.data.conflicts || []);
    } catch (e) { console.error(e); }
  };

  const openReview = (target, layer, action) => {
    setReviewTarget(target);
    setReviewLayer(layer);
    setReviewAction(action);
    setReviewReason('');
    setReviewOpen(true);
  };

  const submitReview = async () => {
    try {
      const uid = reviewTarget.verification.user_id;
      await api.post(`/admin/verify/${uid}/${reviewLayer}`, { action: reviewAction, reason: reviewReason });
      toast.success(`${reviewLayer.toUpperCase()} ${reviewAction}d for ${reviewTarget.user?.alias}`);
      setReviewOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const grantLevel = async (userId, level) => {
    if (!window.confirm(`Grant Level ${level} to this user?`)) return;
    try {
      await api.post(`/admin/verify/${userId}/grant?level=${level}`, { action: 'approve', reason: 'Manual grant by admin' });
      toast.success(`Level ${level} granted`);
      fetchAll();
    } catch (e) {
      toast.error('Grant failed');
    }
  };

  const approveSource = async (sourceId, approve) => {
    try {
      await api.post(`/admin/sources/${sourceId}/approve?approve=${approve}&note=${encodeURIComponent(approve ? 'Reviewed and verified' : 'Did not meet criteria')}`);
      toast.success(`Source ${approve ? 'approved' : 'rejected'}`);
      fetchAll();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const updatePayout = async (payoutId, status) => {
    try {
      await api.post(`/admin/payouts/${payoutId}/update?status=${status}&note=Admin%20action`);
      toast.success(`Payout marked ${status}`);
      fetchAll();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const RESOLUTION_LABELS = {
    dismissed: 'Dismiss (false positive)',
    seller_a_kept: 'Keep new lead, remove existing',
    seller_b_kept: 'Keep existing, remove new',
    both_removed: 'Remove BOTH leads',
    escalated: 'Escalate for review',
  };

  const askResolveConflict = (conflictId, resolution) => {
    setConflictConfirm({ conflict_id: conflictId, resolution });
  };

  const confirmResolveConflict = async () => {
    if (!conflictConfirm) return;
    try {
      await api.post(`/admin/conflicts/${conflictConfirm.conflict_id}/resolve`, { resolution: conflictConfirm.resolution });
      toast.success('Conflict resolved');
      setConflictConfirm(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolution failed');
      setConflictConfirm(null);
    }
  };

  const resolveConflict = async (conflictId, resolution) => {
    askResolveConflict(conflictId, resolution);
  };

  const totalRevenue = transactions.filter(t => t.payment_status === 'paid').reduce((s, t) => s + t.amount, 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-purple-50 text-purple-800 border border-purple-200 rounded text-xs font-mono font-semibold">
            <Shield className="w-3 h-3" /> ADMIN ACCESS
          </span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Full visibility into real identities, transactions, and disputes.</p>
          </div>
          <button
            onClick={() => navigate('/admin/framework')}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 hover:bg-gray-50 font-medium"
            data-testid="admin-open-framework-btn"
          >
            Framework administration →
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mt-8 mb-8">
          <Stat label="Total Users" value={users.length} icon={Users} />
          <Stat label="Active Leads" value={leads.length} icon={Tag} />
          <Stat label="Transactions" value={transactions.length} icon={Receipt} />
          <Stat label="Revenue" value={`$${totalRevenue.toFixed(2)}`} icon={Building2} />
        </div>

        <Tabs defaultValue="ops" data-testid="admin-tabs">
          <TabsList className="bg-gray-100 p-1 flex-wrap h-auto">
            <TabsTrigger value="ops" data-testid="admin-tab-ops">
              <Activity className="w-3.5 h-3.5 mr-1.5" /> Ops
            </TabsTrigger>
            <TabsTrigger value="inspector" data-testid="admin-tab-inspector">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Inspector
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts">
              Payouts
              {payouts.filter(p => p.status === 'pending').length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono">
                  {payouts.filter(p => p.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sources" data-testid="admin-tab-sources">
              Sources
              {sources.filter(s => s.approval_status === 'pending').length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono">
                  {sources.filter(s => s.approval_status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="disputes" data-testid="admin-tab-disputes">
              Disputes
              {disputes.filter(d => d.status === 'open').length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-mono">
                  {disputes.filter(d => d.status === 'open').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="flagged" data-testid="admin-tab-flagged">
              Flagged
              {flaggedMsgs.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-mono">
                  {flaggedMsgs.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="verify" data-testid="admin-tab-verify">              Verification
              {verifyQueue.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono">
                  {verifyQueue.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="conflicts" data-testid="admin-tab-conflicts">
              Conflicts
              {conflicts.filter(c => c.status === 'open').length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono">
                  {conflicts.filter(c => c.status === 'open').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Logs</TabsTrigger>
          </TabsList>

          {/* Ops Overview */}
          <TabsContent value="ops" className="mt-6">
            <AdminOpsOverview />
          </TabsContent>

          {/* Transaction Inspector */}
          <TabsContent value="inspector" className="mt-6">
            <AdminTransactionInspector />
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Alias</th>
                    <th className="px-4 py-3">Real Name</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">ABN</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Trust</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map(u => (
                    <tr key={u.user_id} className="hover:bg-gray-50" data-testid={`admin-user-${u.user_id}`}>
                      <td className="px-4 py-3"><AliasBadge alias={u.alias} clickable={true} userId={u.user_id} /></td>
                      <td className="px-4 py-3 font-medium">{u.name}</td>
                      <td className="px-4 py-3 text-gray-700">{u.company_name || '-'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{u.abn || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{u.email}</td>
                      <td className="px-4 py-3 font-mono text-xs">{u.phone || '-'}</td>
                      <td className="px-4 py-3"><span className="text-xs uppercase tracking-wider text-gray-600">{u.role}</span></td>
                      <td className="px-4 py-3 font-mono text-xs">{u.subscription_plan || 'beta_free'}</td>
                      <td className="px-4 py-3"><TrustBadge score={u.trust_score} size="sm" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Leads */}
          <TabsContent value="leads" className="mt-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Lead ID</th>
                    <th className="px-4 py-3">Seller</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Region</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map(l => (
                    <tr key={l.lead_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{l.lead_id}</td>
                      <td className="px-4 py-3"><AliasBadge alias={l.seller_alias} /></td>
                      <td className="px-4 py-3 font-medium">{l.category}</td>
                      <td className="px-4 py-3">{l.region}</td>
                      <td className="px-4 py-3 text-gray-600">{l.source_type}</td>
                      <td className="px-4 py-3 font-mono">${l.price?.toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Transactions */}
          <TabsContent value="transactions" className="mt-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Transaction ID</th>
                    <th className="px-4 py-3">Lead ID</th>
                    <th className="px-4 py-3">Buyer</th>
                    <th className="px-4 py-3">Seller</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transactions.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">No transactions yet</td></tr>
                  ) : transactions.map(t => (
                    <tr key={t.transaction_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{t.transaction_id}</td>
                      <td className="px-4 py-3 font-mono text-xs">{t.lead_id}</td>
                      <td className="px-4 py-3 text-xs font-mono">{t.buyer_id}</td>
                      <td className="px-4 py-3 text-xs font-mono">{t.seller_id}</td>
                      <td className="px-4 py-3 font-mono font-semibold">${t.amount?.toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={t.payment_status} /></td>
                      <td className="px-4 py-3">
                        {t.payment_status === 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700 border-red-200 hover:bg-red-50 text-xs h-7"
                            onClick={async () => {
                              const reason = window.prompt('Refund reason?', 'requested_by_customer');
                              if (!reason) return;
                              try {
                                await api.post(`/admin/transactions/${t.transaction_id}/refund`, { reason, note: '' });
                                toast.success('Refund + transfer reversal issued');
                                fetchAll();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Refund failed');
                              }
                            }}
                            data-testid={`refund-btn-${t.transaction_id}`}
                          >
                            Refund
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Disputes */}
          <TabsContent value="disputes" className="mt-6">
            {disputes.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                No disputes raised
              </div>
            ) : (
              <div className="space-y-3">
                {disputes.map(d => (
                  <div key={d.dispute_id} className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-mono text-xs text-gray-400">{d.dispute_id}</div>
                        <div className="font-heading text-base font-semibold mt-1">{d.reason}</div>
                      </div>
                      <StatusBadge status={d.status} />
                    </div>
                    <div className="text-sm text-gray-700 mb-3">{d.description}</div>
                    <div className="flex gap-4 text-xs font-mono text-gray-500">
                      <span>Txn: {d.transaction_id}</span>
                      <span>By: {d.raised_by}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Payouts */}
          <TabsContent value="payouts" className="mt-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Payout ID</th>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-4 py-3">Seller</th>
                    <th className="px-4 py-3">Gross</th>
                    <th className="px-4 py-3">Net</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payouts.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">No payouts yet</td></tr>
                  ) : payouts.map(p => (
                    <tr key={p.payout_id} className="hover:bg-gray-50" data-testid={`admin-payout-${p.payout_id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{p.payout_id}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.lead_id}</td>
                      <td className="px-4 py-3 text-xs font-mono">{p.seller_id}</td>
                      <td className="px-4 py-3 font-mono">${p.gross_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono font-semibold">${p.net_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {p.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => updatePayout(p.payout_id, 'approved')} data-testid={`approve-payout-${p.payout_id}`}>
                              Approve
                            </Button>
                          )}
                          {p.status === 'approved' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs h-7"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/payouts/${p.payout_id}/transfer`);
                                    toast.success('Transfer sent to Stripe Connect');
                                    fetchAll();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Transfer failed');
                                  }
                                }}
                                data-testid={`transfer-payout-${p.payout_id}`}
                              >
                                Transfer via Stripe
                              </Button>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updatePayout(p.payout_id, 'paid')} data-testid={`pay-payout-${p.payout_id}`}>
                                Mark Paid
                              </Button>
                            </>
                          )}
                          {(p.status === 'pending' || p.status === 'approved') && (
                            <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => updatePayout(p.payout_id, 'cancelled')}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Sources */}
          <TabsContent value="sources" className="mt-6">
            {sources.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                No sources registered yet
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-sources-list">
                {sources.map(s => (
                  <div key={s.source_id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`admin-source-${s.source_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <AliasBadge alias={s.seller_alias} />
                        <div>
                          <div className="font-heading text-base font-semibold">{s.name}</div>
                          <div className="font-mono text-xs text-gray-500 mt-0.5">{s.source_id}</div>
                        </div>
                      </div>
                      <StatusBadge status={s.approval_status} />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                      <div><span className="text-gray-500">Type:</span> <span className="font-medium">{s.source_type}</span></div>
                      <div><span className="text-gray-500">Category:</span> <span className="font-medium">{s.category}</span></div>
                      <div><span className="text-gray-500">Region:</span> <span className="font-medium">{s.region}</span></div>
                    </div>
                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100">{s.description}</div>
                    {s.approval_status === 'pending' && (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approveSource(s.source_id, true)} data-testid={`approve-source-${s.source_id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => approveSource(s.source_id, false)} data-testid={`reject-source-${s.source_id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Flagged Messages */}
          <TabsContent value="flagged" className="mt-6">
            {flaggedMsgs.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                No flagged messages
              </div>
            ) : (
              <div className="space-y-3" data-testid="flagged-messages-list">
                {flaggedMsgs.map(m => (
                  <div key={m.message_id} className="bg-white border border-red-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <AliasBadge alias={m.sender_alias} />
                        <div>
                          <div className="font-mono text-xs text-gray-500">{m.message_id}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Room: <span className="font-mono">{m.room_id}</span></div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {m.flag_reasons?.map(r => (
                          <span key={r} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded font-mono uppercase tracking-wider">
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Original (admin only)</div>
                      <div className="text-sm text-gray-900 bg-red-50 p-3 rounded border border-red-100">{m.original_text}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Redacted (shown to other party)</div>
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">{m.redacted_text}</div>
                    </div>
                    <div className="mt-2 text-xs font-mono text-gray-400">
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Verification Queue */}
          <TabsContent value="verify" className="mt-6">
            {verifyQueue.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                Verification queue is clear
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-verify-queue">
                {verifyQueue.map(entry => {
                  const v = entry.verification;
                  const u = entry.user;
                  if (!u) return null;
                  return (
                    <div key={v.user_id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`admin-verify-${v.user_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <AliasBadge alias={u.alias} />
                          <div>
                            <div className="font-heading text-base font-semibold">{u.name}</div>
                            <div className="text-xs text-gray-500">{u.email} · {u.role}</div>
                          </div>
                        </div>
                        <TrustLevelBadge level={v.trust_level} />
                      </div>

                      {v.kyc_status === 'pending' && (
                        <div className="mb-3 border border-blue-200 bg-blue-50 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-blue-800">KYC — Identity Review</div>
                            <StatusBadge status="pending" />
                          </div>
                          {v.kyc_data && (
                            <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                              <div><span className="text-gray-500">Full name:</span> {v.kyc_data.full_name}</div>
                              <div><span className="text-gray-500">DOB:</span> {v.kyc_data.date_of_birth}</div>
                              <div><span className="text-gray-500">ID:</span> {v.kyc_data.id_type} ••••{(v.kyc_data.id_number||'').slice(-4)}</div>
                              <div><span className="text-gray-500">Mobile:</span> {v.kyc_data.mobile}</div>
                              <div className="col-span-2"><span className="text-gray-500">Address:</span> {v.kyc_data.address_line1}, {v.kyc_data.suburb} {v.kyc_data.state} {v.kyc_data.postcode}</div>
                            </div>
                          )}
                          <div className="mt-3 flex gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openReview(entry, 'kyc', 'approve')} data-testid={`kyc-approve-${v.user_id}`}>Approve KYC</Button>
                            <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => openReview(entry, 'kyc', 'reject')} data-testid={`kyc-reject-${v.user_id}`}>Reject</Button>
                          </div>
                        </div>
                      )}

                      {v.kyb_status === 'pending' && (
                        <div className="mb-3 border border-green-200 bg-green-50 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold uppercase tracking-wider text-green-800">KYB — Business Review</div>
                            <StatusBadge status="pending" />
                          </div>
                          {v.kyb_data && (
                            <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                              <div><span className="text-gray-500">Business:</span> {v.kyb_data.business_name}</div>
                              <div><span className="text-gray-500">ABN:</span> <span className="font-mono">{v.kyb_data.abn}</span></div>
                              <div><span className="text-gray-500">ACN:</span> <span className="font-mono">{v.kyb_data.acn || '-'}</span></div>
                              <div><span className="text-gray-500">Entity:</span> {v.kyb_data.entity_type || '-'}</div>
                              <div><span className="text-gray-500">GST:</span> {v.kyb_data.gst_registered ? 'Registered' : 'No'}</div>
                              <div><span className="text-gray-500">Age:</span> {v.kyb_data.business_age_years ? `${v.kyb_data.business_age_years}y` : '-'}</div>
                              <div className="col-span-2"><span className="text-gray-500">Contact:</span> {v.kyb_data.business_email} · {v.kyb_data.business_phone}</div>
                              {v.kyb_data.website && <div className="col-span-2"><span className="text-gray-500">Website:</span> {v.kyb_data.website}</div>}
                              {v.kyb_data.auto_flag && (
                                <div className="col-span-2 text-red-700 font-mono text-[10px] uppercase tracking-wider">⚠ Auto-flag: {v.kyb_data.auto_flag}</div>
                              )}
                            </div>
                          )}
                          <div className="mt-3 flex gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openReview(entry, 'kyb', 'approve')} data-testid={`kyb-approve-${v.user_id}`}>Approve KYB</Button>
                            <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => openReview(entry, 'kyb', 'reject')} data-testid={`kyb-reject-${v.user_id}`}>Reject</Button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Manual grant:</span>
                        {!v.premium_grant && (
                          <Button size="sm" variant="outline" onClick={() => grantLevel(v.user_id, 3)} data-testid={`grant-premium-${v.user_id}`}>Grant Premium (L3)</Button>
                        )}
                        {!v.enterprise_grant && (
                          <Button size="sm" variant="outline" onClick={() => grantLevel(v.user_id, 4)} data-testid={`grant-enterprise-${v.user_id}`}>Grant Enterprise (L4)</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Duplicate Detection Conflict Queue */}
          <TabsContent value="conflicts" className="mt-6">
            {conflicts.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No duplicate-detection conflicts
                <div className="text-xs text-gray-400 mt-2">Cross-seller and same-seller SHA-256 contact matches will appear here.</div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-conflicts-list">
                {conflicts.map(c => (
                  <div key={c.conflict_id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`admin-conflict-${c.conflict_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-gray-500">{c.conflict_id}</span>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono ${
                            c.status === 'open' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                            c.status === 'dismissed' ? 'bg-gray-100 text-gray-600' :
                            'bg-blue-50 text-[#0044CC] border border-[#0055FF]/20'
                          }`}>
                            {c.status.replace(/_/g,' ')}
                          </span>
                          {c.cross_seller && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono bg-red-50 text-red-700 border border-red-200">
                              Cross-seller
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-mono bg-gray-100 text-gray-700">
                            match: {c.match_type}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          Contact hash: {c.contact_hash?.slice(0, 16)}…
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {new Date(c.created_at).toLocaleString()}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {c.involved_leads?.map((l, i) => (
                        <div key={l.lead_id} className={`border rounded p-3 ${
                          l.role === 'new' ? 'border-[#0055FF]/30 bg-[#0055FF]/5' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">
                              {l.role === 'new' ? 'New submission' : 'Existing lead'}
                            </span>
                            <AliasBadge alias={l.seller_alias} />
                          </div>
                          <div className="text-sm font-mono text-gray-900">{l.lead_id}</div>
                          <div className="text-xs text-[#0044CC] font-mono mt-0.5">{l.digital_title}</div>
                          {l.role === 'existing' && (
                            <div className="text-xs text-gray-500 mt-1">
                              status: {l.status} · sold: {l.sold_count ?? 0}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {c.status === 'open' && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                        <Button size="sm" variant="outline" onClick={() => resolveConflict(c.conflict_id, 'dismissed')} data-testid={`conflict-dismiss-${c.conflict_id}`}>
                          Dismiss (false positive)
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resolveConflict(c.conflict_id, 'seller_a_kept')} data-testid={`conflict-keep-new-${c.conflict_id}`}>
                          Keep new
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resolveConflict(c.conflict_id, 'seller_b_kept')} data-testid={`conflict-keep-existing-${c.conflict_id}`}>
                          Keep existing
                        </Button>
                        <Button size="sm" variant="outline" className="text-gray-800" onClick={() => resolveConflict(c.conflict_id, 'both_removed')} data-testid={`conflict-remove-both-${c.conflict_id}`}>
                          Remove both
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resolveConflict(c.conflict_id, 'escalated')} data-testid={`conflict-escalate-${c.conflict_id}`}>
                          Escalate
                        </Button>
                      </div>
                    )}
                    {c.resolution_note && (
                      <div className="mt-2 text-xs text-gray-500 italic">Note: {c.resolution_note}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Audit Logs */}
          <TabsContent value="audit" className="mt-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Resource</th>
                    <th className="px-4 py-3">Metadata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-8 text-gray-500">No audit logs yet</td></tr>
                  ) : auditLogs.map(log => (
                    <tr key={log.log_id} className="hover:bg-gray-50" data-testid={`audit-log-${log.log_id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{log.user_id}</td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{log.action}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {log.resource_type}/{log.resource_id}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 max-w-md truncate">
                        {JSON.stringify(log.metadata)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Verification review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{reviewAction === 'approve' ? 'Approve' : 'Reject'} {reviewLayer.toUpperCase()}</DialogTitle>
            <DialogDescription>
              {reviewTarget?.user?.alias} — {reviewTarget?.user?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {reviewAction === 'approve' ? 'Notes (optional)' : 'Rejection reason (required)'}
            </label>
            <Textarea
              value={reviewReason}
              onChange={e => setReviewReason(e.target.value)}
              placeholder={reviewAction === 'reject' ? 'e.g. Address does not match ID document' : 'Verified via provider XYZ'}
              data-testid="review-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button
              onClick={submitReview}
              className={reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              disabled={reviewAction === 'reject' && !reviewReason.trim()}
              data-testid="review-submit-btn"
            >
              {reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict resolution confirmation */}
      <AlertDialog open={!!conflictConfirm} onOpenChange={(v) => !v && setConflictConfirm(null)}>
        <AlertDialogContent data-testid="conflict-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {conflictConfirm ? RESOLUTION_LABELS[conflictConfirm.resolution] : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action is recorded to the immutable ledger and cannot be undone. Sold leads are protected from archive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conflict-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResolveConflict}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="conflict-confirm-submit"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
    </div>
    <div className="font-mono text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);
